/*
    The definition behind a column heading.

    A number somebody does not believe is the failure this report is most likely to have, and
    the answer is usually one paragraph long. Putting it behind the heading itself means
    nobody has to know the methodology section exists to find it.

    A plain button and an absolutely positioned panel rather than a tooltip library: the text
    runs to several paragraphs with formula blocks in it, which is more than a tooltip should
    ever hold, and it has to stay open while somebody reads it.
*/
import { useEffect, useRef, useState } from "react";

import { MethodBody } from "./MethodologyPanel";
import { methodFor } from "./methodology";

export default function ColumnInfo({ column }: { column: string }) {
    const [open, setOpen] = useState(false);
    const wrapper = useRef<HTMLSpanElement>(null);
    const entry = methodFor(column);

    useEffect(() => {
        if (!open) return undefined;
        const dismiss = (event: MouseEvent | KeyboardEvent) => {
            if (event instanceof KeyboardEvent) {
                if (event.key === "Escape") setOpen(false);
                return;
            }
            if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", dismiss);
        document.addEventListener("keydown", dismiss);
        return () => {
            document.removeEventListener("mousedown", dismiss);
            document.removeEventListener("keydown", dismiss);
        };
    }, [open]);

    // No definition is not a bug: Client and Campaign need none. Rendering nothing keeps the
    // icon meaningful, so where it appears there is always something behind it.
    if (!entry) return null;

    return (
        <span ref={wrapper} className="position-relative d-inline-block">
            {/* The design system's info icon, the one the Budget page uses on "Remaining
                Flight Funding". It was U+24D8, which renders with a bar under the "i" in most
                faces and so did not match anything else in the platform (Holly, QA,
                7 Oct 2026). */}
            <button type="button"
                className="holdout-report__info"
                aria-label={`How ${entry.field} is calculated`}
                aria-expanded={open}
                onClick={() => setOpen(!open)}>
                <i className="bi bi-info-circle" />
            </button>
            {open && (
                <div className="position-absolute bg-white border rounded shadow-sm p-3 text-start"
                    style={{ width: 320, zIndex: 1080, top: "1.4rem", left: 0,
                             whiteSpace: "normal", fontWeight: 400 }}
                    role="dialog"
                    aria-label={entry.field}>
                    <div className="holdout-report__method-field">{entry.field}</div>
                    <MethodBody entry={entry} />
                </div>
            )}
        </span>
    );
}
